import { useEffect, useMemo, useRef } from 'react';
import I18n from 'I18n';
import { generatePath, matchPath, useHistory, useParams } from 'react-router-dom';
import { Button } from 'foundations-components/transitional/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { mergeDeepWithLocalStorage } from 'automation-ui-canvas/actions/LocalStorageActions';
import * as LocalStorageKeys from 'automation-ui-canvas/constants/LocalStorageKeys';
import PortalIdParser from 'PortalIdParser';
import { getZoomLevel, isMinimapMinimized } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { useIsUngated, useUserId } from 'automation-ui-core/hooks/authHooks';
import { useCollaborationSidebarState } from 'collaboration-sidebar';
import { CANVAS_ROOT_TEST, FLOW_VIEW_ROOT, REVISION_PREVIEW } from 'automation-ui-canvas/constants/AbsolutePaths';
import RevisionRollupPanel from 'automation-ui-management/revision-rollup-panel/RevisionRollupPanel';
import { NavMarker } from 'react-rhumb';
import FlowReferencesPanel from 'automation-ui-management/components/workflow-references/FlowReferencesPanel';
import OpenViewConnectionsPanelButton from 'automation-ui-management/components/OpenViewConnectionsPanelButton';
import { getActionLogsUrl, getMetricsUrl, getEnrollmentHistoryListUrl, getPerformanceUrl } from 'automation-ui-canvas/utils/urls';
import { OPEN_QUERY_PARAM } from 'automation-ui-canvas/components/flow-header/menu-bar/MenuBar';
import DebuggerPanel from 'automation-ui-canvas/components/panels/debugger-panel/DebuggerPanel';
import { DEBUGGER, TEST_V2 } from 'automation-ui-canvas/constants/Gates';
import { SingleActionTestPanel } from 'automation-ui-canvas/components/panels/debugger-panel/SingleActionTestPanel';
import TestFlowViewConfig from 'automation-ui-canvas/containers/flow-views/TestFlowViewConfig';
import { getIsObjectlessFlow } from 'automation-ui-flow-core/flow/flowUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const useCommentsOption = () => {
  return useMemo(() => ({
    id: 'comments',
    getLabel: () => I18n.text('automation.editorMenuBar.options.comments'),
    ModalComponent: ({
      onClose
    }) => {
      const isMounted = useRef(false);
      const {
        isOpen,
        openSidebar,
        closeSidebar,
        onUpdateCollaborationSidebarState
      } = useCollaborationSidebarState();

      /*
      Ensure that onclose gets called when panel closes. The order of these effects matters
      greatly since swapping will cause isMounted to be set by the time that this effect fires
      */
      useEffect(() => {
        /* Is mounted ensures this only applies after initial mount, otherwise onClose will get called during the initial panel opening phase */
        if (!isOpen && isMounted.current) {
          onClose();
        }
      }, [isOpen, onClose]);

      /* Main effect (like didMount) ensures that the sidebar gets opened on mount, and closed on unmount */
      useEffect(() => {
        openSidebar();
        isMounted.current = true;
        return () => {
          closeSidebar();
          onUpdateCollaborationSidebarState({
            isCommentModeActive: false
          });
        };
      }, [openSidebar, closeSidebar, onUpdateCollaborationSidebarState]);
      return null;
    },
    shortcut: [{
      shift: true,
      option: true,
      key: 'C'
    },
    // Because we are avoiding using `code` to support international key layouts this is the best we can do to detect the shortcut
    // Rely on C for the labels and ç for the detection
    {
      shift: true,
      option: true,
      key: 'Ç'
    }]
  }), []);
};
export const useZoomOption = ({
  flow
}) => {
  const dispatch = useDispatch();
  const portalId = PortalIdParser.get();
  const zoomLevel = useSelector(getZoomLevel(flow.flowId));
  const isMinimapVisible = useSelector(isMinimapMinimized);
  const userId = useUserId();
  return {
    id: 'zoom',
    getLabel: () => I18n.text('automation.editorMenuBar.options.zoom'),
    options: [[{
      id: 'zoomIn',
      getLabel: () => I18n.text('automation.editorMenuBar.options.zoomIn'),
      onClick: () => {
        dispatch(mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.ZOOM_LEVEL}-${portalId}-${flow.flowId}`]: Math.min(zoomLevel + 0.1, 1.0)
        }));
      },
      ButtonComponent: buttonProps => {
        return /*#__PURE__*/_jsx(Button, Object.assign({}, buttonProps, {
          disabled: zoomLevel >= 1.0
        }));
      },
      /* This seems to be common for shortcuts. The short cut will say "Control +" but mean "Control =" based on the way the user understands the symbols on the keys. The user is actually pressing = but thinking they are pressing + */
      shortcut: [{
        control: true,
        key: '+'
      }, {
        control: true,
        key: '='
      }]
    }, {
      id: 'zoomOut',
      getLabel: () => I18n.text('automation.editorMenuBar.options.zoomOut'),
      onClick: () => {
        dispatch(mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.ZOOM_LEVEL}-${portalId}-${flow.flowId}`]: Math.max(zoomLevel - 0.1, 0.3)
        }));
      },
      ButtonComponent: buttonProps => {
        return /*#__PURE__*/_jsx(Button, Object.assign({}, buttonProps, {
          disabled: zoomLevel <= 0.3
        }));
      },
      shortcut: {
        control: true,
        key: '-'
      }
    }, {
      id: 'zoomFull',
      getLabel: () => I18n.text('automation.editorMenuBar.options.zoomFull'),
      onClick: () => {
        dispatch(mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.ZOOM_LEVEL}-${portalId}-${flow.flowId}`]: 1.0
        }));
      },
      shortcut: {
        control: true,
        key: '0'
      }
    }, {
      id: 'zoomHalf',
      getLabel: () => I18n.text('automation.editorMenuBar.options.zoomHalf'),
      onClick: () => {
        dispatch(mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.ZOOM_LEVEL}-${portalId}-${flow.flowId}`]: 0.5
        }));
      },
      shortcut: {
        control: true,
        key: '9'
      }
    }, {
      id: 'minimap',
      getLabel: () => I18n.text('automation.editorMenuBar.options.minimap'),
      onClick: () => {
        dispatch(mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.MINIMAP_MINIMIZED}-${portalId}-${userId}`]: !isMinimapVisible
        }));
      }
    }]]
  };
};
export const useEnrollmentHistoryOption = ({
  flow
}) => {
  const history = useHistory();
  return {
    id: 'enrollmentHistoryPage',
    getLabel: () => I18n.text('automation.editorMenuBar.options.enrollmentHistory'),
    onClick: () => {
      history.push({
        pathname: getEnrollmentHistoryListUrl({
          flowId: flow.flowId
        }),
        search: `?showVersions=true`
      });
    }
  };
};
export const MetricsOption = {
  id: 'metrics',
  getLabel: () => I18n.text('automation.editorMenuBar.options.metrics'),
  onClick: ({
    history,
    flow
  }) => {
    const search = new URLSearchParams(history.location.search);
    search.set('showVersions', 'true');
    history.push({
      pathname: getMetricsUrl({
        flowId: flow.flowId
      }),
      search: search.toString()
    });
  }
};
export const TestV2Option = {
  id: 'test',
  getLabel: () => I18n.text('automation.editorMenuBar.options.test'),
  getIsVisible: ({
    getIsUngated,
    flow
  }) => getIsUngated(TEST_V2) && !getIsObjectlessFlow(flow === null || flow === void 0 ? void 0 : flow.toHybridFlowInterface()),
  onClick: ({
    history,
    flow
  }) => {
    const search = new URLSearchParams(history.location.search);
    if (flow.flowId) {
      history.push({
        pathname: generatePath(FLOW_VIEW_ROOT, {
          flowId: flow.flowId,
          viewId: TestFlowViewConfig.id
        }),
        search: search.toString()
      });
    }
  }
};
export const TestOption = {
  id: 'test',
  getLabel: () => I18n.text('automation.editorMenuBar.options.test'),
  ModalComponent: ({
    flow,
    onClose
  }) => {
    const history = useHistory();
    useEffect(() => {
      /* Handle case where modal is dismissed via the close button */
      if (!matchPath(history.location.pathname, [CANVAS_ROOT_TEST])) {
        onClose();
      }
    }, [history, onClose]);
    useEffect(() => {
      const search = new URLSearchParams(history.location.search);
      search.set(OPEN_QUERY_PARAM, 'test');
      history.push({
        pathname: generatePath(CANVAS_ROOT_TEST, {
          flowId: flow.flowId
        }),
        search: search.toString()
      });
    }, [flow, history]);
    return null;
  }
};
export const DebuggerOption = {
  id: 'debugger',
  getLabel: () => I18n.text('automation.editorMenuBar.options.debugger'),
  badge: 'new',
  ModalComponent: ({
    onClose,
    flow
  }) => {
    return /*#__PURE__*/_jsx(DebuggerPanel, {
      onClose: onClose,
      flow: flow
    });
  },
  getIsVisible: ({
    getIsUngated
  }) => getIsUngated(DEBUGGER)
};
export const SingleActionTestOption = {
  id: 'singleActionTest',
  getLabel: () => I18n.text('automation.editorMenuBar.options.singleActionTest'),
  badge: 'new',
  ModalComponent: ({
    onClose,
    flow
  }) => {
    const params = useParams();
    const actionId = params.actionId;
    return /*#__PURE__*/_jsx(SingleActionTestPanel, {
      actionId: actionId,
      onClose: onClose,
      flow: flow
    });
  },
  getIsVisible: () => false
};
export const VersionHistoryOption = {
  id: 'version',
  getLabel: () => I18n.text('automation.editorMenuBar.options.version'),
  ModalComponent: ({
    onClose,
    flow
  }) => {
    const history = useHistory();
    return /*#__PURE__*/_jsx(RevisionRollupPanel, {
      onClose: onClose,
      onRevisionSelect: evt => {
        const params = new URLSearchParams(history.location.search);
        params.set(OPEN_QUERY_PARAM, 'version');
        history.replace({
          pathname: generatePath(REVISION_PREVIEW, {
            flowId: flow.flowId,
            revisionId: evt.target.value
          }),
          search: params.toString()
        });
      },
      renderNavMarker: () => /*#__PURE__*/_jsx(NavMarker, {
        name: "REVISION_ROLLUP_PANEL_LOADED"
      }),
      renderFailureNavMarker: () => /*#__PURE__*/_jsx(NavMarker, {
        name: "REVISION_ROLLUP_PANEL_FAILED"
      })
    });
  }
};
export const PerformanceOption = {
  id: 'performance',
  getLabel: () => I18n.text('automation.editorMenuBar.options.performance'),
  external: true,
  onClick: ({
    flow
  }) => {
    window.open(getPerformanceUrl({
      flowId: flow.flowId
    }), '_blank');
  }
};
export const RECENTLY_ENROLLED_PARAM = 'recentlyEnrolled';
export const ActionLogsOption = {
  id: 'actionLogs',
  getLabel: () => I18n.text('automation.editorMenuBar.options.actionLogs'),
  external: true,
  onClick: ({
    flow
  }) => {
    window.open(getActionLogsUrl({
      flowId: flow.flowId
    }), '_blank');
  }
};
export const ConnectionsOption = {
  id: 'connections',
  getLabel: () => I18n.text('automation.editorMenuBar.options.connections'),
  ButtonComponent: props => {
    return /*#__PURE__*/_jsx(OpenViewConnectionsPanelButton, Object.assign({}, props));
  },
  ModalComponent: ({
    flow,
    onClose
  }) => {
    return /*#__PURE__*/_jsx(FlowReferencesPanel, {
      flowId: Number(flow.flowId),
      onClose: onClose,
      flowName: flow.name || undefined
    });
  }
};
export const useViewOption = ({
  flow
}) => {
  const isUngatedForTestV2 = useIsUngated(TEST_V2);
  const CommentsOption = useCommentsOption({
    flow
  });
  const ZoomOption = useZoomOption({
    flow
  });
  const EnrollmentHistoryOption = useEnrollmentHistoryOption({
    flow
  });
  const options = useMemo(() => [[CommentsOption, ZoomOption, isUngatedForTestV2 ? TestV2Option : TestOption, DebuggerOption, SingleActionTestOption, ConnectionsOption], [VersionHistoryOption, PerformanceOption, EnrollmentHistoryOption, MetricsOption, ActionLogsOption]], [CommentsOption, ZoomOption, EnrollmentHistoryOption, isUngatedForTestV2]);
  return useMemo(() => ({
    id: 'view',
    getLabel: () => I18n.text('automation.editorMenuBar.menus.view'),
    options
  }), [options]);
};
export const useEnrollmentHistoryViewOption = ({
  flow
}) => {
  const ZoomOption = useZoomOption({
    flow
  });
  const EnrollmentHistoryOption = useEnrollmentHistoryOption({
    flow
  });
  const options = useMemo(() => [[ZoomOption], [VersionHistoryOption, PerformanceOption, EnrollmentHistoryOption, MetricsOption]], [ZoomOption, EnrollmentHistoryOption]);
  return useMemo(() => ({
    id: 'view',
    getLabel: () => I18n.text('automation.editorMenuBar.menus.view'),
    options
  }), [options]);
};
export const useRevisionViewOption = ({
  flow
}) => {
  const ZoomOption = useZoomOption({
    flow
  });
  const EnrollmentHistoryOption = useEnrollmentHistoryOption({
    flow
  });
  const options = useMemo(() => [[ZoomOption], [VersionHistoryOption, PerformanceOption, ActionLogsOption, EnrollmentHistoryOption, MetricsOption]], [ZoomOption, EnrollmentHistoryOption]);
  return useMemo(() => ({
    id: 'view',
    getLabel: () => I18n.text('automation.editorMenuBar.menus.view'),
    options
  }), [options]);
};